import { admin2 } from './instance'

const memberLevelRuleBaseUrl = (shopId) => `/${shopId}/memberLevelRule`

/**
 * 取得會員等級規則（升等 / 續等 / 進度條設定）admin.memberLevelRule.singleFind
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const GetMemberLevelRule = async ({ shopId }) => {
  return await admin2({
    method: 'GET',
    url: memberLevelRuleBaseUrl(shopId),
  })
}

/**
 * 建立/更新會員等級規則 admin.memberLevelRule.singleUpdate
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @param {Array} params.upgradeRules 升等規則陣列
 * @param {Array} params.renewalRules 續等/降等規則陣列
 * @param {object} params.progressBarSettings 進度條設定
 * @param {object} [params.supplementaryText] 補充說明文字設定
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const UpdateMemberLevelRule = async ({ shopId, upgradeRules, renewalRules, progressBarSettings, supplementaryText }) => {
  return await admin2({
    method: 'PUT',
    url: memberLevelRuleBaseUrl(shopId),
    data: { upgradeRules, renewalRules, progressBarSettings, supplementaryText },
  })
}

/**
 * 查詢可用交易來源 admin.memberLevelRule.availableSources
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 * @example
 * // 回傳格式
 * { availableSources: [{ source: 'APPOINTMENT_ORDER', displayName: '預約訂單', enabled: true }] }
 */
export const GetMemberLevelRuleAvailableSources = async ({ shopId }) => {
  return await admin2({
    method: 'GET',
    url: `${memberLevelRuleBaseUrl(shopId)}/availableSources`,
  })
}

/**
 * 預覽規則影響（各等級人數統計）admin.memberLevelRule.preview
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @param {Array} params.upgradeRules 升等規則陣列
 * @param {Array} params.renewalRules 續等/降等規則陣列
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const PreviewMemberLevelRule = async ({ shopId, upgradeRules, renewalRules }) => {
  return await admin2({
    method: 'POST',
    url: `${memberLevelRuleBaseUrl(shopId)}/preview`,
    data: { upgradeRules, renewalRules },
  })
}

const upgradeConfigBaseUrl = (shopId) => `/${shopId}/memberLevelUpgradeConfig`
const renewalConfigBaseUrl = (shopId) => `/${shopId}/memberLevelRenewalConfig`
const downgradeConfigBaseUrl = (shopId) => `/${shopId}/memberLevelDowngradeConfig`
const progressBarConfigBaseUrl = (shopId) => `/${shopId}/memberLevelProgressBarConfig`

/**
 * 取得會員升等設定
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const GetMemberLevelUpgradeConfig = async ({ shopId }) => {
  return await admin2({
    method: 'GET',
    url: upgradeConfigBaseUrl(shopId),
  })
}

/**
 * 更新會員升等設定
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @param {number} params.level 目標等級
 * @param {object} params.data 更新資料
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const UpdateMemberLevelUpgradeConfig = async ({ shopId, level, ...data }) => {
  return await admin2({
    method: 'PUT',
    url: `${upgradeConfigBaseUrl(shopId)}/${level}`,
    data,
  })
}

/**
 * 取得會員續等設定
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const GetMemberLevelRenewalConfig = async ({ shopId }) => {
  return await admin2({
    method: 'GET',
    url: renewalConfigBaseUrl(shopId),
  })
}

/**
 * 更新會員續等設定
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @param {number} params.level 等級
 * @param {object} params.data 更新資料
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const UpdateMemberLevelRenewalConfig = async ({ shopId, level, ...data }) => {
  return await admin2({
    method: 'PUT',
    url: `${renewalConfigBaseUrl(shopId)}/${level}`,
    data,
  })
}

/**
 * 取得會員降等設定
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const GetMemberLevelDowngradeConfig = async ({ shopId }) => {
  return await admin2({
    method: 'GET',
    url: downgradeConfigBaseUrl(shopId),
  })
}

/**
 * 更新會員降等設定
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @param {number} params.fromLevel 來源等級（較高等級）
 * @param {number} params.toLevel 目標等級（較低等級）
 * @param {object} params.data 更新資料
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const UpdateMemberLevelDowngradeConfig = async ({ shopId, fromLevel, toLevel, ...data }) => {
  return await admin2({
    method: 'PUT',
    url: `${downgradeConfigBaseUrl(shopId)}/${fromLevel}/to/${toLevel}`,
    data,
  })
}

/**
 * 取得會員進度條顯示設定
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const GetMemberLevelProgressBarConfig = async ({ shopId }) => {
  return await admin2({
    method: 'GET',
    url: progressBarConfigBaseUrl(shopId),
  })
}

/**
 * 更新會員進度條顯示設定
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @param {object} params.data 更新資料
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const UpdateMemberLevelProgressBarConfig = async ({ shopId, ...data }) => {
  return await admin2({
    method: 'PUT',
    url: progressBarConfigBaseUrl(shopId),
    data,
  })
}

/**
 * 更新會員進度條補充說明文字設定
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @param {boolean} params.enabled 是否啟用
 * @param {string} params.content 補充說明文字內容
 * @param {'clientHome'|'clientLevelDetail'|'both'} params.displayLocation 顯示位置
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const UpdateMemberLevelProgressBarSupplementaryText = async ({ shopId, ...data }) => {
  return await admin2({
    method: 'PUT',
    url: `${progressBarConfigBaseUrl(shopId)}/supplementaryText`,
    data,
  })
}

/**
 * 更新指定來源等級的升等進度條顯示設定
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @param {number} params.currentLevel 會員目前等級
 * @param {Array} params.items 進度條設定項目列表
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const UpdateMemberLevelProgressBarUpgradeProgress = async ({ shopId, currentLevel, items }) => {
  return await admin2({
    method: 'PUT',
    url: `${progressBarConfigBaseUrl(shopId)}/upgradeProgress/${currentLevel}`,
    data: { items },
  })
}

/**
 * 更新指定來源等級的續等進度條顯示設定
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @param {number} params.currentLevel 會員目前等級
 * @param {Array} params.items 進度條設定項目列表
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const UpdateMemberLevelProgressBarRenewalProgress = async ({ shopId, currentLevel, items }) => {
  return await admin2({
    method: 'PUT',
    url: `${progressBarConfigBaseUrl(shopId)}/renewalProgress/${currentLevel}`,
    data: { items },
  })
}

const upgradeConditionBaseUrl = (shopId, fromLevel, toLevel) =>
  `/${shopId}/memberLevelUpgradeConfig/${toLevel}/condition`

/**
 * 新增升等條件
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @param {number} params.fromLevel 來源等級
 * @param {number} params.toLevel 目標等級
 * @param {string} params.conditionType 升等條件類型
 * @param {number} params.value 條件數值（金額 / 件數 / 次數）
 * @param {number|null} params.accumulationPeriod 最大累計區間（月），累計型條件適用
 * @param {string|null} params.channel 指定管道，指定品項條件適用
 * @param {string} params.name 條件名稱（自動產生）
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const CreateMemberLevelUpgradeCondition = async ({ shopId, fromLevel, toLevel, ...data }) => {
  return await admin2({
    method: 'POST',
    url: upgradeConditionBaseUrl(shopId, fromLevel, toLevel),
    data: { fromLevel, ...data },
  })
}

/**
 * 更新升等條件
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @param {number} params.fromLevel 來源等級
 * @param {number} params.toLevel 目標等級
 * @param {string} params.conditionId 條件 ID
 * @param {string} params.conditionType 升等條件類型
 * @param {number} params.value 條件數值
 * @param {number|null} params.accumulationPeriod 最大累計區間（月）
 * @param {string|null} params.channel 指定管道
 * @param {string} params.name 條件名稱
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const UpdateMemberLevelUpgradeCondition = async ({ shopId, fromLevel, toLevel, conditionId, ...data }) => {
  return await admin2({
    method: 'PUT',
    url: `${upgradeConditionBaseUrl(shopId, fromLevel, toLevel)}/${conditionId}`,
    data: { fromLevel, ...data },
  })
}

/**
 * 刪除升等條件
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @param {number} params.fromLevel 來源等級
 * @param {number} params.toLevel 目標等級
 * @param {string} params.conditionId 條件 ID
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const DeleteMemberLevelUpgradeCondition = async ({ shopId, fromLevel, toLevel, conditionId }) => {
  return await admin2({
    method: 'DELETE',
    url: `${upgradeConditionBaseUrl(shopId, fromLevel, toLevel)}/${conditionId}`,
  })
}

const memberLevelBenefitDescriptionBaseUrl = (shopId) => `/${shopId}/memberLevelBenefitDescription`

/**
 * 取得等級權益說明設定 admin.memberLevelBenefitDescription.singleFind
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const GetMemberLevelBenefitDescription = async ({ shopId }) => {
  return await admin2({
    method: 'GET',
    url: memberLevelBenefitDescriptionBaseUrl(shopId),
  })
}

/**
 * 更新等級權益說明設定 admin.memberLevelBenefitDescription.singleUpdate
 * @param {object} params
 * @param {string} params.shopId 店家 ID
 * @param {boolean} params.enabled 是否啟用（顯示於顧客端）
 * @param {string|null} params.pageTitle 頁面標題
 * @param {Array} params.blocks 內容區塊陣列
 * @param {string} params.blocks[].blockId 區塊 ID
 * @param {'text'|'image'} params.blocks[].type 區塊類型
 * @param {number} params.blocks[].order 排列順序
 * @param {string|null} [params.blocks[].title] 標題
 * @param {string|null} [params.blocks[].content] 文字內容（type='text'）
 * @param {string|null} [params.blocks[].textBackgroundColor] 文字背景色（type='text'）
 * @param {string|null} [params.blocks[].imageUrl] 圖片網址（type='image'）
 * @param {string|null} [params.blocks[].ImageId] 圖片 ID（type='image'）
 * @returns {Promise<Array>} [data, errMsg, rawErr]
 */
export const UpdateMemberLevelBenefitDescription = async ({ shopId, enabled, pageTitle, blocks }) => {
  return await admin2({
    method: 'PUT',
    url: memberLevelBenefitDescriptionBaseUrl(shopId),
    data: { enabled, pageTitle, blocks },
  })
}
